import { useState, useEffect, useMemo } from 'react';
import { Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { ProductCard } from './ProductCard.jsx';
import { FilterSlot } from '../../registry/slots/FilterSlot.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select.jsx';
import { useTenant } from '../../context/TenantContext.jsx';
import { SHOP_CATEGORIES } from '../../lib/shop-taxonomy.js';
import { getProducts } from '../../lib/api.js';
import { getApiErrorCode } from '../../lib/api-error.js';
import { useAslCategoryTree } from '../../lib/use-asl-category-tree.js';
import { getAslNodeByKey, parseAslCategoryQuery, resolveAslSlugPath } from '../../lib/asl-tree.js';
import { buildSearchSubmitUrl, buildCanonicalAslUrl } from '../../lib/asl-url.js';

const ASL_FILTER_ERROR_MESSAGES = {
  invalid: 'Die ASL-Kategorie in der URL ist ungültig.',
  invalid_asl_category: 'Die ASL-Kategorie ist ungültig.',
  repeated: 'Mehrere ASL-Kategorien in der URL.',
  repeated_asl_category: 'Die URL enthält mehrere ASL-Kategorien.',
  unknown: 'Die ASL-Kategorie ist unbekannt oder nicht verfügbar.',
  unknown_asl_category: 'Die ASL-Kategorie ist unbekannt oder nicht verfügbar.',
  legacy_filter_conflict: 'Der ASL-Filter kann nicht mit Legacy-Filtern kombiniert werden.',
  asl_snapshot_unavailable: 'Der ASL-Kategoriebaum ist derzeit nicht verfügbar.',
};

export function getAslFilterErrorMessage(code) {
  return ASL_FILTER_ERROR_MESSAGES[code] || '';
}

export function buildAslCategoryFilterUrl(routeQuery, key) {
  return buildCanonicalAslUrl(routeQuery, key);
}

export function CatalogView({
  route,
  onNavigate,
  onViewProduct,
  onAddToCart,
}) {
  const { tenant } = useTenant();
  const { tree: aslTree, categories: apiCategories, status: treeStatus } = useAslCategoryTree(tenant?.id);
  const categorySlugFromRoute = route?.params?.categorySlug || route?.categorySlug || '';
  const subcategorySlugFromRoute = route?.params?.subcategorySlug || route?.subcategorySlug || '';
  const routeQuery = route?.query || route?.searchParams || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams());
  const aslQuery = parseAslCategoryQuery(routeQuery);
  const legacyPath = [categorySlugFromRoute, subcategorySlugFromRoute].filter(Boolean);
  const legacyAslNode = treeStatus === 'ready' && legacyPath.length ? resolveAslSlugPath(aslTree, legacyPath) : null;
  const selectedAslCategory = aslQuery.key || legacyAslNode?.key || '';
  const selectedAslNode = selectedAslCategory ? getAslNodeByKey(aslTree, selectedAslCategory) : null;
  const aslFilterError = aslQuery.error || (aslQuery.key && treeStatus === 'ready' && !selectedAslNode ? 'unknown' : '');
  const searchParamFromRoute = routeQuery.get('search') || routeQuery.get('query') || '';

  const [search, setSearch] = useState(searchParamFromRoute);
  const [selectedCategory, setSelectedCategory] = useState(categorySlugFromRoute);
  const [selectedSubcategory, setSelectedSubcategory] = useState(subcategorySlugFromRoute);
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [sort, setSort] = useState('recommended');
  const [page, setPage] = useState(1);
  const [limit] = useState(24);

  const [products, setProducts] = useState([]);
  const [subCounts, setSubCounts] = useState({});
  const [manufacturers, setManufacturers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiAslError, setApiAslError] = useState('');

  const filterLayout = tenant?.settings?.filter_layout || 'sidebar';
  const isTopbar = filterLayout === 'topbar';
  const cardVariant = tenant?.settings?.product_card_variant || 'specs_heavy';

  // Sync state from route props when URL changes
  useEffect(() => {
    setSelectedCategory(categorySlugFromRoute);
    setSelectedSubcategory(subcategorySlugFromRoute);
    setSearch(searchParamFromRoute);
    setPage(1);
  }, [categorySlugFromRoute, subcategorySlugFromRoute, searchParamFromRoute]);


  // Fetch products
  useEffect(() => {
    let isMounted = true;
    async function fetchCatalog() {
      if (aslFilterError) {
        setApiAslError('');
        setProducts([]); setTotalCount(0); setLoading(false); return;
      }
      setLoading(true);
      setApiAslError('');
      try {
        const res = await getProducts({
          tenantId: tenant?.id,
          aslCategory: selectedAslCategory || undefined,
          category: selectedAslCategory ? undefined : selectedCategory,
          subcategory: selectedAslCategory ? undefined : selectedSubcategory,
          search: search,
          manufacturer: selectedManufacturer,
          sort,
          page,
          limit,
        });

        const errorCode = res?.error?.code;
        if (errorCode && getAslFilterErrorMessage(errorCode)) {
          if (isMounted) {
            setApiAslError(errorCode);
            setProducts([]);
            setTotalCount(0);
          }
          return;
        }

        if (isMounted) {
          const items = res.products || res.items || [];

          setProducts(items);
          setTotalCount(res.pagination?.total || res.total || items.length);

          // Subcategory counts for the current (filtered) result set
          if (Array.isArray(res.subcategories)) {
            setSubCounts(Object.fromEntries(res.subcategories.map(s => [s.slug, s.count])));
          }

          // Prefer server-side manufacturer facet (full result set);
          // fall back to page-local extraction only if the API lacks it.
          if (Array.isArray(res.manufacturers) && res.manufacturers.length > 0) {
            setManufacturers(res.manufacturers);
          } else {
            const mfSet = new Set();
            items.forEach(p => {
              if (p.manufacturer) mfSet.add(p.manufacturer);
            });
            setManufacturers(Array.from(mfSet).sort());
          }
        }
      } catch (err) {
        const errorCode = getApiErrorCode(err);
        if (isMounted) {
          if (getAslFilterErrorMessage(errorCode)) setApiAslError(errorCode);
          else console.warn('Error fetching catalog products:', err);
          setProducts([]);
          setTotalCount(0);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchCatalog();
    return () => { isMounted = false; };
  }, [tenant?.id, selectedCategory, selectedSubcategory, selectedAslCategory, aslFilterError, search, sort, page, limit, selectedManufacturer]);

  const handleCategorySelect = (catSlug) => {
    setSelectedCategory(catSlug);
    setSelectedSubcategory('');
    setPage(1);
    const newPath = catSlug ? `/catalog/${catSlug}` : '/catalog';
    onNavigate(newPath);
  };

  const handleAslCategorySelect = (key) => {
    const url = buildAslCategoryFilterUrl(routeQuery, key);
    setPage(1);
    if (onNavigate) onNavigate(url);
    else if (typeof window !== 'undefined') window.history.pushState(null, '', url);
  };

  useEffect(() => {
    if (treeStatus !== 'ready' || !legacyAslNode || aslQuery.error || aslQuery.key) return;
    // Legacy-Redirect ist kein Nutzerklick: Suchbegriff bleibt erhalten.
    const url = buildCanonicalAslUrl(routeQuery, legacyAslNode.key, { keepSearch: true });
    if (onNavigate) onNavigate(url, { replace: true });
    else if (typeof window !== 'undefined') window.history.replaceState(null, '', url);
  }, [treeStatus, legacyAslNode?.key, aslQuery.key, aslQuery.error, routeQuery, onNavigate]);

  const handleSubcategorySelect = (subSlug) => {
    setSelectedSubcategory(subSlug);
    setPage(1);
    if (selectedCategory) onNavigate(`/catalog/${selectedCategory}/${subSlug}`);
  };

  const handleSearchSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setPage(1);
    onNavigate(buildSearchSubmitUrl(routeQuery, search, selectedCategory));
  };

  const handleResetFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedManufacturer('');
    setPage(1);
    onNavigate('/catalog');
  };

  const activeCategoryObj = selectedAslNode;
  // Legacy-Filter ohne auflösbaren ASL-Knoten behalten ihre sprechenden Namen.
  const legacyStaticCategory = selectedCategory
    ? SHOP_CATEGORIES.find((category) => category.slug === selectedCategory) ?? null
    : null;
  const legacyApiCategory = selectedCategory
    ? apiCategories.find((category) => category.slug === selectedCategory) ?? null
    : null;
  const legacyApiSubcategory = !selectedAslNode && selectedSubcategory
    ? apiCategories.find((category) => category.slug === selectedSubcategory && category.parentId != null) ?? null
    : null;
  const legacyApiSubcategoryParent = legacyApiSubcategory
    ? apiCategories.find((category) => String(category.id) === String(legacyApiSubcategory.parentId)) ?? null
    : null;
  const legacyStaticSubcategory = legacyStaticCategory?.subcategories?.find((subcategory) => subcategory.slug === selectedSubcategory) ?? null;
  const legacyCategoryName = !selectedAslNode && selectedCategory
    ? (legacyApiCategory?.name
      ?? legacyApiCategory?.label
      ?? legacyApiSubcategoryParent?.name
      ?? legacyStaticCategory?.label
      ?? legacyStaticCategory?.name
      ?? null)
    : null;
  const legacySubcategoryName = !selectedAslNode && selectedSubcategory
    ? (legacyApiSubcategory?.name
      ?? legacyApiSubcategory?.label
      ?? legacyStaticSubcategory?.label
      ?? legacyStaticSubcategory?.name
      ?? null)
    : null;
  const legacyBreadcrumbCategoryName = !selectedSubcategory || legacySubcategoryName
    ? legacyCategoryName
    : null;
  const legacyTitleName = selectedSubcategory ? legacySubcategoryName : legacyCategoryName;
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const enrichedLegacyCategories = useMemo(() => {
    const apiBySlug = new Map(apiCategories.map((category) => [category.slug, category]));
    return SHOP_CATEGORIES.map((staticCategory) => {
      const apiCategory = apiBySlug.get(staticCategory.slug);
      return {
        ...staticCategory,
        productCount: apiCategory?.productCount ?? apiCategory?.product_count ?? 0,
        subcategories: (staticCategory.subcategories || []).map((subcategory) => ({
          ...subcategory,
          count: subCounts[subcategory.slug] ?? 0,
        })),
      };
    });
  }, [apiCategories, subCounts]);
  const enrichedCategories = selectedAslCategory ? [] : enrichedLegacyCategories;

  // Grid style depending on card variant and layout
  const gridClasses = cardVariant === 'compact_row'
    ? 'grid gap-2.5 grid-cols-1'
    : isTopbar
    ? 'grid gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4'
    : 'grid gap-6 sm:grid-cols-2 xl:grid-cols-3';

  return (
    <div className="mx-auto w-full max-w-[1920px] px-4 py-8 sm:px-6 lg:px-8 space-y-8">
      
      {/* Top Header & Breadcrumbs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[var(--border)] pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-[var(--secondary)] mb-1">
            <button onClick={() => onNavigate('/')} className="hover:text-[var(--primary)] cursor-pointer">Startseite</button>
            <span>/</span>
            <button onClick={() => handleCategorySelect('')} className="hover:text-[var(--primary)] cursor-pointer">Katalog</button>
            {selectedAslNode && <><span>/</span><span className="text-[var(--primary)] font-bold">{selectedAslNode.name}</span></>}
            {legacyBreadcrumbCategoryName && <><span>/</span><span className="text-[var(--primary)] font-bold">{legacyBreadcrumbCategoryName}</span></>}
            {legacySubcategoryName && <><span>/</span><span className="text-[var(--primary)] font-bold">{legacySubcategoryName}</span></>}
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-4xl">
            {activeCategoryObj ? activeCategoryObj.name : (legacyTitleName || 'Gesamtes Sortiment')}
          </h1>
          {(aslFilterError || apiAslError) && <p role="alert" data-asl-filter-error className="mt-2 text-sm font-semibold text-[var(--primary)]">{getAslFilterErrorMessage(aslFilterError || apiAslError)}</p>}
          {treeStatus === 'unavailable' && <p role="status">Kategorien sind derzeit nicht verfügbar.</p>}
          <p className="text-sm text-[var(--secondary)] mt-1">
            {loading && totalCount === 0
              ? 'Artikel werden geladen …'
              : <>{totalCount} {totalCount === 1 ? 'Artikel verfügbar' : 'Artikel im Sortiment'}</>}
          </p>
        </div>

        {/* Sort selector (Shown in header if sidebar mode, or handled in topbar if topbar mode) */}
        {!isTopbar && (
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-[var(--secondary)] shrink-0">Sortierung:</span>
            <div className="w-48">
              <Select value={sort} onValueChange={(val) => { setSort(val); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Sortieren nach..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recommended">Empfehlung</SelectItem>
                  <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                  <SelectItem value="price_asc">Preis (aufsteigend)</SelectItem>
                  <SelectItem value="price_desc">Preis (absteigend)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* If Topbar Filter Layout is active, render Topbar here */}
      {isTopbar && (
        <FilterSlot
          variant="topbar"
          tenant={tenant}
          categories={enrichedCategories}
          activeCategory={selectedCategory}
          activeSubcategory={selectedSubcategory}
          aslTree={aslTree}
          activeAslCategory={selectedAslCategory}
          onSelectAslCategory={handleAslCategorySelect}
          onSelectCategory={handleCategorySelect}
          onSelectSubcategory={handleSubcategorySelect}
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearchSubmit}
          manufacturers={manufacturers}
          selectedManufacturer={selectedManufacturer}
          onSelectManufacturer={setSelectedManufacturer}
          sort={sort}
          onSortChange={(val) => { setSort(val); setPage(1); }}
          onResetFilters={handleResetFilters}
        />
      )}

      {/* Main Grid: Sidebar Filters + Products or Full Width */}
      <div className={isTopbar ? 'space-y-8' : 'grid gap-8 lg:grid-cols-[260px_1fr]'}>
        
        {/* Left Sidebar Filter (if not topbar) */}
        {!isTopbar && (
          <FilterSlot
            variant="sidebar"
            tenant={tenant}
            categories={enrichedCategories}
            activeCategory={selectedCategory}
            activeSubcategory={selectedSubcategory}
          aslTree={aslTree}
          activeAslCategory={selectedAslCategory}
          onSelectAslCategory={handleAslCategorySelect}
            onSelectCategory={handleCategorySelect}
            onSelectSubcategory={handleSubcategorySelect}
            search={search}
            onSearchChange={setSearch}
            onSearchSubmit={handleSearchSubmit}
            manufacturers={manufacturers}
            selectedManufacturer={selectedManufacturer}
            onSelectManufacturer={setSelectedManufacturer}
            sort={sort}
            onSortChange={(val) => { setSort(val); setPage(1); }}
            onResetFilters={handleResetFilters}
          />
        )}

        {/* Product Grid Area */}
        <section className="space-y-8">
          
          {loading ? (
            <div className={gridClasses}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="rounded-2xl border border-[var(--border)] bg-white p-4 space-y-4">
                  <Skeleton className="aspect-square w-full rounded-xl" />
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <div className="flex justify-between items-center pt-2">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-10 w-10 rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-3xl border border-[var(--border)] bg-white p-12 text-center space-y-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--surface-container-low)] text-[var(--secondary)]">
                <Package className="h-8 w-8" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-[var(--foreground)]">Keine Artikel gefunden</h3>
                <p className="text-sm text-[var(--secondary)] mt-1 max-w-md">
                  Für Ihre aktuelle Auswahl oder Ihren Suchbegriff konnten leider keine passenden Produkte gefunden werden.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleResetFilters}
              >
                Filter zurücksetzen
              </Button>
            </div>
          ) : (
            <div className={gridClasses}>
              {products.map((product, idx) => (
                <ProductCard
                  key={product.id || product.sku}
                  product={product}
                  index={idx}
                  onViewProduct={(slugOrId) => {
                    if (onViewProduct) onViewProduct(slugOrId);
                    else onNavigate(`/products/${slugOrId}`);
                  }}
                  onAddToCart={onAddToCart}
                />
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[var(--border)] pt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="gap-1 cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                Vorherige
              </Button>

              <span className="text-xs font-semibold text-[var(--secondary)]">
                Seite {page} von {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="gap-1 cursor-pointer"
              >
                Nächste
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

        </section>

      </div>
    </div>
  );
}
